import { ChangeEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Button, IconButton, InputBar, Pagination, Spinner } from '~/ui';
import '~/assets/css/select-table.css';
import styles from './UsersBoard.module.scss';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import UserBoardActions from '../UserBoardActions/UserBoardActions';
import { UsersTable } from './UsersTable/UsersTable';
import { Modal } from '~/components/Shared/Modal/Modal';
import ChangeUsersStatusModalContent from './ChangeUsersStatusModalContent/ChangeUsersStatusModalContent';
import { useToastSuccess } from '~/utils/useToastSuccess';
import UserFilters from './UserFilters/UserFilters';
import FiltersIcon from '~/assets/svg/newSvg/filter.svg?react';
import { IUsersFilters } from '~/utils/types/userFilterTypes';
import { getPaginate } from '~/utils/getPaginate';
import Actions, { Action } from '~/ui/Actions';
import Dropdown from '~/components/Shared/Dropdown';
import { UserAccuralContent } from './UserAccuralContent/UserAccuralContent';

interface SelectedPage {
  selected: number;
}

interface Points {
  page: number;
}

export interface IBlockCategory {
  id: number;
  name: string;
  type: string;
  display_name: string;
}

interface Props {
  userTripStatus: string;
}

export const UsersBoard = ({ userTripStatus }: Props) => {
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<Points>({
      initialValues: { page: 15 },
      key: 'users-points',
      exclude: [],
    });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { paginate } = getPaginate();
  const [page, setPage] = useState<number>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<IUsersFilters | {}>({});
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<{
    block: boolean;
    unblock: boolean;
  }>({
    block: false,
    unblock: false,
  });
  const [isStatusUsersModalOpen, setIsStatusUsersModalOpen] = useState(false);
  const [accrualUsersModalOpen, setAccrualUsersModalOpen] = useState(false);
  const [accrualType, setAccrualType] = useState<'add' | 'subtract' | ''>('');
  const client = useQueryClient();
  const [selectedSections, setSelectedSections] = useState<IBlockCategory[]>(
    []
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();

  const handleToggleSelectedSections = (section: IBlockCategory) => {
    if (
      selectedSections.find(
        selectedSection => selectedSection.id === section.id
      )
    ) {
      const filteredSections = selectedSections.filter(
        selectedSection => selectedSection.id !== section.id
      );
      setSelectedSections(filteredSections);
    } else {
      setSelectedSections([...selectedSections, section]);
    }
  };

  const handleBlockUsers = async () => {
    setIsLoading({ ...isLoading, block: true });
    try {
      await instance.post('admin/block-list/block', {
        user_ids: selectedUsers,
        category_ids: selectedSections.map(section => section.id),
      });
      setIsStatusUsersModalOpen(false);
      setSelectedUsers([]);
      client.invalidateQueries(['users']);
      toastSuccess(t('employees_blocked'));
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading({ ...isLoading, block: false });
    }
  };

  const handleUnblockUsers = async () => {
    setIsLoading({ ...isLoading, unblock: true });
    try {
      await instance.post('admin/block-list/unblock', {
        user_ids: selectedUsers,
        category_ids: selectedSections.map(section => section.id),
      });
      setIsStatusUsersModalOpen(false);
      setSelectedUsers([]);
      client.invalidateQueries(['users']);
      toastSuccess(t('employees_blocked'));
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading({ ...isLoading, unblock: false });
    }
  };

  const params = {
    sort: sort.direction,
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
    business_trip: userTripStatus,
    filters,
  };

  const { data, isError, error, isFetching } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/users', {
        params,
      });
      return response.data;
    },
    queryKey: ['users', params],
    keepPreviousData: true,
  });

  const { mutateAsync: handleUserBlock } = useMutation(
    async (value: boolean) => {
      const result = await instance.post(`admin/block-user-many`, {
        user_ids: selectedUsers,
        block: value,
      });
      return result.data;
    },
    {
      onSuccess: () => {
        setSelectedUsers([]);
        client.invalidateQueries(['users']);
        toastSuccess(t('employee_status_successfully_updated'));
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const users = data?.users?.data;

  const { mutateAsync: handleUserAccural } = useMutation(
    async (points: number) => {
      const response = await instance.post('admin/user/set-points', {
        user_ids: selectedUsers,
        action: accrualType,
        points: points,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toastSuccess(
          accrualType === 'add'
            ? t('points_successfully_added')
            : t('points_successfully_written_off')
        );
        setSelectedUsers([]);
        setAccrualUsersModalOpen(false);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  let content: React.ReactNode;

  const handleSelect = (id: number) => {
    if (selectedUsers.includes(id)) {
      const filteredUsers = selectedUsers.filter(
        selectedId => selectedId !== id
      );
      setSelectedUsers(filteredUsers);
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedUsers.length === data?.users?.data.length) {
      setSelectedUsers([]);
    } else {
      const userIdArray = data?.users?.data.map((user: any) => user.id);
      setSelectedUsers(userIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('admin/users-many', {
        data: { user_ids: selectedUsers },
      });
      setSelectedUsers([]);
      toastSuccess(t('users_deleted'));
      client.invalidateQueries(['users']);
    } catch (error) {
      toastError(error);
    }
  };

  const handleOpenChangeUser = () => {
    setIsStatusUsersModalOpen(true);
  };

  if (users) {
    content = (
      <UsersTable
        selectedItems={selectedUsers}
        handleToggle={handleSelect}
        handleToggleAll={handleToggleAll}
        users={users}
        sort={sort}
        setSort={setSort}
      />
    );
  } else if (isError) {
    content = <ErrorMessage error={error} />;
  } else {
    content = <Spinner />;
  }

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  useEffect(() => {
    setPage(paginate);
  }, [paginate]);

  return (
    <Board>
      <div className={styles.usersTableOptions}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <div className={styles.tableHeadRightWrapper}>
          {!!selectedUsers.length && (
            <Dropdown caption={t('actions')}>
              <Actions>
                <Action variant="base" onClick={() => handleUserBlock(false)}>
                  {t('unblock')}
                </Action>
                <Action variant="danger" onClick={() => handleUserBlock(true)}>
                  {t('block')}
                </Action>
                <Action
                  variant="base"
                  onClick={() => {
                    setAccrualUsersModalOpen(true);
                    setAccrualType('add');
                  }}
                >
                  {t('accrual_points')}
                </Action>
                <Action
                  variant="danger"
                  onClick={() => {
                    setAccrualUsersModalOpen(true);
                    setAccrualType('subtract');
                  }}
                >
                  {t('debiting_points')}
                </Action>
              </Actions>
            </Dropdown>
          )}
          {!!selectedUsers.length && (
            <Button
              text={t('delete')}
              color="red"
              onClick={handleDeleteSelected}
              className={styles.deleteButton}
            />
          )}
          <InputBar
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            value={searchTerm}
            placeholder={t('search_by_code_n_np')}
            classNameInput={styles.usersTableInput}
            label={`${t('search')}:`}
            star={false}
            classNameInputWrapper={styles.usersTableInputWrapper}
          />
          <IconButton
            className={styles.filtersButton}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            <FiltersIcon />{' '}
            <span className={styles.filtersButtonText}>
              {isFiltersOpen ? `${t('hide_filters')}` : `${t('show_filters')}`}
            </span>
          </IconButton>
          <UserBoardActions />
        </div>
      </div>
      {isFiltersOpen ? (
        <UserFilters
          isUsersLoading={isFetching}
          props={''}
          selectedUsers={selectedUsers}
          setIsStatusUsersModalOpen={handleOpenChangeUser}
          setSelectedUsers={() => setSelectedUsers([])}
          onClick={setFilters}
        />
      ) : null}
      <div className={styles.contentWrapper}>{content}</div>
      <div className={styles.usersTablePagination}>
        <TableAmountPage
          firstRow={data?.meta?.from}
          lastRow={data?.meta?.to}
          total={data?.meta?.total}
        />
        <Pagination
          forcePage={paginate}
          pageCount={data?.meta?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
      <Modal
        isOpen={accrualUsersModalOpen}
        onClose={() => setAccrualUsersModalOpen(false)}
        modalStyles={styles.blockModal}
      >
        <UserAccuralContent
          type={accrualType}
          onSubmit={handleUserAccural}
          onClose={() => setAccrualUsersModalOpen(false)}
          accrualUsersModalOpen={accrualUsersModalOpen}
        />
      </Modal>
      <Modal
        isOpen={isStatusUsersModalOpen}
        onClose={() => setIsStatusUsersModalOpen(false)}
        modalStyles={styles.blockModal}
      >
        <ChangeUsersStatusModalContent
          isLoading={isLoading}
          selectedSections={selectedSections}
          onCancelPress={() => setIsStatusUsersModalOpen(false)}
          handleSubmitUnblock={handleUnblockUsers}
          handleToggleCheckbox={handleToggleSelectedSections}
          handleSubmit={handleBlockUsers}
        />
      </Modal>
    </Board>
  );
};
